import type { Modal } from '@ps/web-core-store';
import { Size } from '@ps/web-core-store';
import type { ClassNameProp } from '@ps/web-shared-components';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MemoryRouter, Route, matchPath, useLocation } from 'react-router';

import StarsModalRoutes from '@swf/components/StarsModalRoutes/StarsModalRoutes';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { encodeQuery } from '@swf/core/utils';

import BaseModal from './BaseModal';
import styles from './StarsModal.scss';

export interface State extends ClassNameProp {
  size: Size;
  hidden?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEsc?: boolean;
  overlayClassName?: string;
  transparentBackground?: boolean;
}

const defaultState: State = {
  size: Size.SMALL,
  hidden: false,
  closeOnOverlayClick: undefined,
  closeOnEsc: undefined,
  overlayClassName: '',
  transparentBackground: false,
  className: ''
};

const StarsModal: React.FC<React.PropsWithChildren> = () => {
  const [
    {
      className,
      overlayClassName,
      transparentBackground,
      hidden,
      size,
      closeOnOverlayClick,
      closeOnEsc
    },
    setState
  ] = useState<State>(defaultState);
  const { path, locationState } = (useSelector(select.modal.getCurrent) || {}) as Partial<Modal>;
  const { modal } = useDispatch<Dispatch>();
  const location = useLocation();

  const search = !isEmpty(locationState) ? encodeQuery(locationState as object) : location.search;

  const setStyle = useCallback(
    (newState: Partial<State> = {}) => setState({ ...defaultState, ...newState }),
    []
  );

  useEffect(() => {
    if (!path) setState(defaultState);
  }, [path]);

  return useMemo(
    () => (
      <MemoryRouter
        keyLength={process.env.NODE_ENV === 'test' ? 0 : 6}
        initialEntries={[{ pathname: path, state: locationState, search }]}
      >
        <Route
          render={({ location: { pathname } }) => (
            <BaseModal
              key={pathname ? pathname.split('/')[0] : undefined}
              className={classNames(styles.modal, className)}
              overlayClassName={overlayClassName}
              enableTransparentBackground={transparentBackground}
              isOpen={!!path}
              onRequestClose={() => modal.close()}
              size={size}
              hidden={hidden}
              fullScreen={!!matchPath('/casino/player-selector', pathname)}
              showAnimation={!hidden}
              closeOnOverlayClick={closeOnOverlayClick}
              closeOnEsc={closeOnEsc}
            >
              <StarsModalRoutes setStyle={setStyle} />
            </BaseModal>
          )}
        />
      </MemoryRouter>
    ),
    [
      path,
      locationState,
      className,
      overlayClassName,
      transparentBackground,
      size,
      hidden,
      closeOnOverlayClick,
      closeOnEsc,
      setStyle,
      modal,
      search
    ]
  );
};

export default StarsModal;
